import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../../shared/helpers/icons';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { Router } from '@angular/router';
import { UserDataService } from '../../../shared/services/userData.service';
import { ICrisisManagement } from '../../../shared/models/crisisManagement';
import { CrisisManagementService } from '../../../shared/services/crisisManagement.service';
import { ContactsService } from '../../../shared/services/contact.service';

@Component({
  selector: 'crisis-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    EopAssistantComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './crisis-management.component.html',
  styleUrl: './crisis-management.component.css'
})
export class CrisisManagementComponent implements OnInit {
  
  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentPageRoute: string | null = null;
  eopSectionTypeId: number | null = null;
  eopSubSectionTypeId: number | null = null;
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];
  pageTitle: string = '';
  description: string = '';  
  crisisSections = [
    'Evacuation Transportation',
    'Parent-Child Relocation Sites',
    'Parent-Child Reunification',
    'Recovery',
    'Physical',
    'Academic',
    'Psychological/Emotional'
  ];
  selectedCrisisSection: string = this.crisisSections[0];
  crisisManagement: ICrisisManagement = {};
  sectionFields: Record<string, { key: keyof ICrisisManagement; label: string }[]> = {
    'Evacuation Transportation': [
      { key: 'primaryEvac', label: 'Primary Evacuation Route:' },
      { key: 'secondaryEvac', label: 'Secondary Evacuation Route:' },
      { key: 'busNumber', label: 'Bus Number:' },
      { key: 'walkPrimaryRoute', label: 'Primary Walkout Route:' },
      { key: 'walkSecondaryRoute', label: 'Secondary Walkout Route:' },
    ],
    'Parent-Child Relocation Sites': [
      { key: 'indoorOnCampus', label: 'Indoor, On-Campus:' },
      { key: 'outdoorOnCampus', label: 'Outdoor, On-Campus:' },
      { key: 'offCampusPrimary', label: 'Off-Campus Primary:' },
      { key: 'offCampusSecondary', label: 'Off-Campus Secondary:' },
    ],
    'Parent-Child Reunification': [
      { key: 'primaryUnification', label: 'Primary Unification Leader:' },
      { key: 'secondaryUnification', label: 'Secondary Unification Leader:' },
      { key: 'reunificationCOA', label: 'Reunification Course of Action:' },
      { key: 'parentSignArea', label: 'Parent sign-in, sign-out area:' },
      { key: 'materialsSupplies', label: 'Materials/Supplies:' },
      { key: 'studentRosters', label: 'Student Rosters:' },
      { key: 'checkoutAuthorizationForms', label: 'Checkout Authorization Forms:' },
      { key: 'staffing', label: 'Staffing:' },
    ],
    'Recovery': [
      { key: 'financialRecords', label: 'Financial Records:' },
      { key: 'emergencyRelief', label: 'Emergency Relief:' },
    ],
    'Physical': [
      { key: 'primaryTeamLead', label: 'Primary Team Leader:' },
      { key: 'secondaryTeamLead', label: 'Secondary Team Leader:' },
      { key: 'documentingSchoolAssets', label: 'Documenting School Assets:' },
      { key: 'studentRecords', label: 'Student Records:' },
      { key: 'facilitySecurity', label: 'Facility Security:' },
      { key: 'divisionOfRiskManagement', label: 'Division of Risk Management, Department of Finance:' },
    ],
    'Academic': [
      { key: 'primeAlternateFacility', label: 'Primary Alternate Facility:' },
      { key: 'secondaryAlternateFacility', label: 'Secondary Alternate Facility:' },
      { key: 'academyMatsandSupplies', label: 'Academy Materials/Supplies:' },
      { key: 'alternateEducational', label: 'Alternate Educational Programs:' },
      { key: 'reconveningCOA', label: 'Reconvening Course of Action:' },
      { key: 'notifyParentsGuardians', label: 'Notification of Parents/Guardians:' },
    ],
    'Psychological/Emotional': [
      { key: 'psyTeamLeader', label: 'Primary Team Leader:' },
      { key: 'psySecondaryTeamLeader', label: 'Secondary Team Leader:' },
      { key: 'traumaGriefPrimary', label: 'Trauma/Grief Counseling Location (Primary):' },
      { key: 'traumaGriefSecondary', label: 'Trauma/Grief Counseling Location (Secondary):	' },
      { key: 'mentalHealthPartner', label: 'Mental Health Partner:' },
      { key: 'faithBasedPartner', label: 'Faith-Based Partner:' },
      { key: 'traumaTrainedStaff', label: 'Trauma-Trained Staff:' },
      { key: 'griefTrainedStaff', label: 'Grief-Trained Staff:' },
      { key: 'immediateShortLongTermCounseling', label: 'Immediate/Short/Long Term Counseling Needs of Student/Staff/Families:' },
      { key: 'notificationOfMissingInjured', label: 'Notification of Missing, Injured, or Death of Family Member:' },
      { key: 'commemorations', label: 'Commemorations, Memorial Activities, Permanent Markers and/or Memorial Structures:' },
    ],
  };

  dropdownFields = [
    'primaryUnification',
    'secondaryUnification',
    'primaryTeamLead',
    'secondaryTeamLead',
    'psyTeamLeader',
    'psySecondaryTeamLeader',
    'mentalHealthPartner',
    'faithBasedPartner',
    'traumaTrainedStaff',
    'griefTrainedStaff'
  ];
  
  
  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private crisisManagementService: CrisisManagementService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();    
    this.loadContacts();
  }

  getCurrentRoute(): string | null {
    const url = this.router.url.replace(/\/+$/, ''); // remove trailing slash
    const segments = url.split('/');
    return segments[-1] || segments[segments.length - 1]; // get last segment
  }

  selectCrisisSection(item: string): void {
    this.selectedCrisisSection = item;
  }

  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        this.contactsList = [...this.fullContactsList];

        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
          this.loadCrisisManagementData();
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  }

  loadCrisisManagementData(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.crisisManagementService.getCrisisManagement(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Crisis Management data loaded:', response);
          if (!response.has_error) {
            this.crisisManagement = response.data || {};
            //Set default contact if empty
            const firstContactId = this.contactsList[0]?.id;
            if (firstContactId) {
              this.dropdownFields.forEach((key) => {
                if (!this.crisisManagement[key as keyof ICrisisManagement]) {
                  this.crisisManagement[key as keyof ICrisisManagement] = firstContactId as any;
                }
              });
            }
          } 
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  }

  onSubmit(): void {
    if (this.currentBuildingId && this.crisisManagement) {
      this.loading = true;    

      // grab only the fields for the selected section
      const allowedKeys = new Set(this.sectionFields[this.selectedCrisisSection].map(f => f.key));
      
      // Prepare payload by converting array-like to object
      const payload = Object.fromEntries(
        Object.entries(this.crisisManagement)
          .filter(([key, value]) =>
            allowedKeys.has(key as keyof ICrisisManagement) &&
            value !== null &&
            value !== undefined &&
            value !== ''
          )
      );

      console.log('Payload for update:', payload);

      this.crisisManagementService.updateCrisisManagement(this.currentBuildingId, payload).subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Crisis Management data saved:', response);
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating Crisis Management.';
            this.loadCrisisManagementData();
          } else {
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message;
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }
  

  handleApproval(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.crisisManagementService.approveCrisisManagement(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Crisis Management approved:', response);
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after approving Crisis Management.';
            this.loadCrisisManagementData();
          } else {
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message;
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }
  

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  isSectionValid(): boolean {
    if (!this.crisisManagement || !this.selectedCrisisSection) return false;
  
    const sectionKeys = this.sectionFields[this.selectedCrisisSection].map(f => f.key);
  
    // check every key has a non-empty value
    return sectionKeys.every(key => {
      const value = (this.crisisManagement as any)[key];
      return value !== null && value !== undefined && value !== '';
    });
  }
  

}
